import { DATE_FORMAT } from "@/constants/datetime";
import { typography } from "@/theme/typography";
import { dayjs } from "@/utils/dayjs";
import { extractTextFromHTML, sanitizeHTML } from "@/utils/pieces";
import { Box, css, Stack, styled, Typography } from "@mui/material";
import { FC, useMemo } from "react";
import { truncateWithEllipsis } from "@/utils/string";
import { TRUNCATE_LENGTH_INFORMATION } from "@/features/information/constants/string";
import { textEllipsis } from "@/theme/minxin";
import { NotificationTag } from "@/components/commons/notification/NotificationTag";

export type TNotificationCardProps = {
  id: string | number;
  time: string;
  type: string;
  title: string;
  description: string;
  filePath: string | null;
  isSingleLine?: boolean;
  isHideContent?: boolean;
};

export const NotificationCard: FC<TNotificationCardProps> = ({ time, type, title, description, isSingleLine, isHideContent }) => {
  const content = useMemo(() => {
    const textFromHtml = extractTextFromHTML(description);

    return (
      <Stack gap={1}>
        {textFromHtml.length > TRUNCATE_LENGTH_INFORMATION ? (
          <Box>{truncateWithEllipsis(textFromHtml, TRUNCATE_LENGTH_INFORMATION)}</Box>
        ) : (
          <Box
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 4,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(description) }}
          />
        )}
      </Stack>
    );
  }, [description]);

  return (
    <Stack gap={1} p={2}>
      <Stack direction="row" alignItems="center" gap={2}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="sub16Semi" fontWeight="400">{dayjs(time).format(DATE_FORMAT)}</Typography>
          <NotificationTag type={type}></NotificationTag>
        </Stack>
        <Typography variant="sub16Semi" flex={1}>
          {title}
        </Typography>
        {isSingleLine && !isHideContent && (
          <Typography variant="sub16Semi" flex={1}>
            {content}
          </Typography>
        )}
      </Stack>
      {!isSingleLine && !isHideContent && <ContentBox>{content}</ContentBox>}
    </Stack>
  );
};

const ContentBox = styled(Box)`
  ${css(typography.body14)};
  max-height: 80px;
  ${css(textEllipsis)}
`;
